import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, CheckCircle2, XCircle, DollarSign, TrendingUp, AlertCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../services/api';

const VerificationSources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    provider: '',
    source_type: 'GOVERNMENT_ID',
    enabled: true,
    cost_per_check: 0,
    base_confidence_score: 0.50,
    quality_tier: 'STANDARD',
    badge_text: '',
    badge_color: 'blue'
  });

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const response = await api.get('/verification-sources');
      setSources(response.data.sources || []);
    } catch (error) {
      console.error('Error fetching sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSource) {
        await api.put(`/verification-sources/${editingSource.id}`, formData);
      } else {
        await api.post('/verification-sources', formData);
      }
      setShowModal(false);
      setEditingSource(null);
      resetForm();
      fetchSources();
    } catch (error) {
      console.error('Error saving source:', error);
      alert('Failed to save verification source');
    }
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setFormData(source);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this verification source?')) return;
    
    try {
      await api.delete(`/verification-sources/${id}`);
      fetchSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      alert('Failed to delete verification source');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      provider: '',
      source_type: 'GOVERNMENT_ID',
      enabled: true,
      cost_per_check: 0,
      base_confidence_score: 0.50,
      quality_tier: 'STANDARD',
      badge_text: '',
      badge_color: 'blue'
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      GOVERNMENT_ID: 'bg-green-100 text-green-800',
      DOCUMENT: 'bg-blue-100 text-blue-800',
      BIOMETRIC: 'bg-orange-100 text-orange-800',
      FINANCIAL: 'bg-purple-100 text-purple-800',
      SOCIAL: 'bg-indigo-100 text-indigo-800',
      ADDRESS: 'bg-pink-100 text-pink-800',
      EMPLOYMENT: 'bg-yellow-100 text-yellow-800',
      CUSTOM: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.CUSTOM;
  };

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', label: 'Active' },
      CONFIGURED: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Configured' },
      NOT_CONFIGURED: { icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Not Configured' },
      ERROR: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Error' },
      DISABLED: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Disabled' }
    };
    return badges[status] || badges.NOT_CONFIGURED;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading verification sources...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verification Sources</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure platform-wide verification sources and their settings
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingSource(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Source
        </button>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 gap-6">
        {sources.map((source) => {
          const statusBadge = getStatusBadge(source.operational_status);
          const StatusIcon = statusBadge.icon;
          const isExpanded = expandedSource === source.id;
          const dataCategories = source.verifiable_data_categories || {};
          const categoriesArray = Object.entries(dataCategories);

          return (
            <div
              key={source.id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{source.name}</h3>
                      <p className="text-sm text-gray-500">{source.provider}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${statusBadge.bg}`}>
                      <StatusIcon className={`w-4 h-4 ${statusBadge.color}`} />
                      <span className={`text-xs font-medium ${statusBadge.color}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                    {source.enabled ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  {source.description || 'No description'}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(source.source_type)}`}>
                      {source.source_type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Cost:</span>
                    <span className="font-medium text-gray-900">£{parseFloat(source.cost_per_check).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Confidence:</span>
                    <span className="font-medium text-gray-900">{(parseFloat(source.base_confidence_score) * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Tier:</span>
                    <span className="font-medium text-gray-900">{source.quality_tier}</span>
                  </div>
                </div>

                {/* Usage Stats */}
                {source.total_verifications_count > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-lg font-semibold text-gray-900">{source.total_verifications_count}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Success</div>
                      <div className="text-lg font-semibold text-green-600">{source.successful_verifications_count}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Failed</div>
                      <div className="text-lg font-semibold text-red-600">{source.failed_verifications_count}</div>
                    </div>
                  </div>
                )}

                {/* Verifiable Data Categories */}
                {categoriesArray.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setExpandedSource(isExpanded ? null : source.id)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      Verifiable Data ({categoriesArray.length} categories)
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-blue-50 rounded-lg">
                        {categoriesArray.map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 capitalize">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-xs text-gray-500">{value.description}</div>
                            </div>
                            <div className="ml-2">
                              <div className="text-xs font-semibold text-blue-600">
                                {(value.confidence * 100).toFixed(0)}%
                              </div>
                              {value.is_primary && (
                                <div className="text-xs text-blue-500">Primary</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 pb-6">
                {!source.is_system && (
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleEdit(source)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(source.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
                {source.is_system && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleEdit(source)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit Settings
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {sources.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No verification sources configured</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingSource ? 'Edit Verification Source' : 'Add Verification Source'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    disabled={editingSource?.is_system}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    disabled={editingSource?.is_system}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Type</label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    disabled={editingSource?.is_system}
                  >
                    <option value="GOVERNMENT_ID">Government ID</option>
                    <option value="DOCUMENT">Document</option>
                    <option value="BIOMETRIC">Biometric</option>
                    <option value="FINANCIAL">Financial</option>
                    <option value="SOCIAL">Social</option>
                    <option value="ADDRESS">Address</option>
                    <option value="EMPLOYMENT">Employment</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Check (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost_per_check}
                    onChange={(e) => setFormData({ ...formData, cost_per_check: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Score</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.base_confidence_score}
                    onChange={(e) => setFormData({ ...formData, base_confidence_score: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quality Tier</label>
                  <select
                    value={formData.quality_tier}
                    onChange={(e) => setFormData({ ...formData, quality_tier: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="BASIC">Basic</option>
                    <option value="STANDARD">Standard</option>
                    <option value="PREMIUM">Premium</option>
                    <option value="ENTERPRISE">Enterprise</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input
                    type="text"
                    value={formData.badge_text}
                    onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Color</label>
                  <input
                    type="text"
                    value={formData.badge_color}
                    onChange={(e) => setFormData({ ...formData, badge_color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                  Enabled
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSource(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingSource ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationSources;

